import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createWorkspaceRealtimeSocket } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';

export type RealtimeEvent = {
  id?: string;
  type: string;
  workspace_id?: string;
  occurred_at?: string;
  payload?: Record<string, unknown>;
  workspaceId?: string;
  resource?: Record<string, unknown>;
};
export type RealtimeStatus = 'connecting' | 'connected' | 'disconnected';
type Listener = (event: RealtimeEvent) => void;

type RealtimeContextValue = {
  socket: WebSocket | null;
  lastEvent: RealtimeEvent | null;
  status: RealtimeStatus;
  subscribe: (listener: Listener) => () => void;
};

const RealtimeContext = createContext<RealtimeContextValue | undefined>(undefined);

export const useRealtime = () => {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error('useRealtime must be used within RealtimeProvider');
  return ctx;
};

export const RealtimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lastEvent, setLastEvent] = useState<RealtimeEvent | null>(null);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [status, setStatus] = useState<RealtimeStatus>('disconnected');
  const listeners = useRef(new Set<Listener>());
  const { token, isAuthenticated } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const subscribe = useCallback((listener: Listener) => { listeners.current.add(listener); return () => listeners.current.delete(listener); }, []);

  useEffect(() => {
    const workspaceId = currentWorkspace?.id;
    if (!isAuthenticated || !token || !workspaceId) { setStatus('disconnected'); setSocket(null); return undefined; }
    let active = true;
    let candidate: WebSocket | null = null;
    let retryTimer: number | undefined;
    let attempt = 0;
    const seenEventIds = new Set<string>();
    const canConnect = () => navigator.onLine !== false && document.visibilityState !== 'hidden';
    const clearTimer = () => { if (retryTimer !== undefined) { window.clearTimeout(retryTimer); retryTimer = undefined; } };
    const detach = (close: boolean) => {
      if (!candidate) return;
      const current = candidate;
      candidate = null;
      current.onopen = null; current.onmessage = null; current.onerror = null; current.onclose = null;
      if (close) current.close();
      setSocket(null);
    };
    const schedule = () => {
      if (!active || retryTimer !== undefined || candidate || !canConnect()) return;
      const base = Math.min(30000, 1000 * (2 ** Math.min(attempt++, 5)));
      const delay = Math.round(base * (0.8 + Math.random() * 0.4));
      retryTimer = window.setTimeout(() => { retryTimer = undefined; connect(); }, delay);
    };
    const connect = () => {
      if (!active || candidate || retryTimer !== undefined || !canConnect()) return;
      setStatus('connecting');
      candidate = createWorkspaceRealtimeSocket(workspaceId, token);
      if (!candidate) { setStatus('disconnected'); return; }
      const opened = candidate;
      setSocket(opened);
      opened.onopen = () => { if (candidate !== opened) return; attempt = 0; setStatus('connected'); };
      opened.onmessage = event => {
        if (candidate !== opened) return;
        try {
          const data = JSON.parse(event.data) as RealtimeEvent;
          if (!data?.type || data.workspace_id !== workspaceId || (data.id && seenEventIds.has(data.id))) return;
          if (data.id) {
            seenEventIds.add(data.id);
            if (seenEventIds.size > 500) seenEventIds.delete(seenEventIds.values().next().value as string);
          }
          setLastEvent(data);
          listeners.current.forEach(listener => listener(data));
        } catch { /* Ignore malformed events. */ }
      };
      opened.onclose = () => { if (!active || candidate !== opened) return; detach(false); setStatus('disconnected'); schedule(); };
      opened.onerror = () => { if (candidate === opened) { detach(true); setStatus('disconnected'); schedule(); } };
    };
    const pause = () => { clearTimer(); detach(true); setStatus('disconnected'); };
    const resume = () => { if (canConnect()) connect(); else pause(); };
    window.addEventListener('offline', pause);
    window.addEventListener('online', resume);
    document.addEventListener('visibilitychange', resume);
    connect();
    return () => {
      active = false; clearTimer(); detach(true); seenEventIds.clear(); setStatus('disconnected');
      window.removeEventListener('offline', pause); window.removeEventListener('online', resume); document.removeEventListener('visibilitychange', resume);
    };
  }, [currentWorkspace?.id, isAuthenticated, token]);

  return <RealtimeContext.Provider value={{ socket, lastEvent, status, subscribe }}>{children}</RealtimeContext.Provider>;
};
